import { useState } from "react";
import { CheckCircle2, Clock, HelpCircle, Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ukDateTime } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";

export const reviewTone = {
  Verified: "bg-[hsl(var(--positive)/0.12)] text-[hsl(var(--positive))]", "N/A": "bg-muted text-muted-foreground",
  Query: "bg-accent text-accent-foreground", Pending: "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]",
};

export function ReviewBadge({ status, testid }) {
  const Icon = status === "Verified" ? CheckCircle2 : status === "Query" ? HelpCircle : Clock;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${reviewTone[status] || "bg-muted"}`} data-testid={testid}><Icon className="h-3 w-3" />{status}</span>;
}

export function Signatures({ signers = [], signatures = [] }) {
  return (
    <ul className="space-y-2" data-testid="signature-list">
      {signers.map((s) => {
        const sig = signatures.find((x) => x.user_id === s.user_id);
        return (
          <li key={s.user_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm" data-testid={`signature-${s.user_id}`}>
            <PenLine className="h-4 w-4 text-muted-foreground" /><b>{s.name}</b><span className="text-muted-foreground">{s.role}</span>
            {sig ? <span className="ml-auto text-xs text-[hsl(var(--positive))]" data-testid="signature-recorded">Signed “{sig.typed_name}” · {ukDateTime(sig.at)}{sig.ip ? ` · IP ${sig.ip}` : ""}</span>
              : <span className="ml-auto text-xs text-muted-foreground">Waiting to sign</span>}
          </li>
        );
      })}
    </ul>
  );
}

export function SignBox({ url, signers = [], signatures = [], onDone, label = "Sign" }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const mine = signers.find((s) => s.user_id === user.id);
  if (!mine || signatures.some((s) => s.user_id === user.id)) return null;
  const go = async () => {
    setBusy(true); setErr("");
    try { await api.post(url, { name, agree }); toast.success("Signed"); onDone(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border-2 border-primary/30 bg-card p-4 space-y-3" data-testid="sign-box">
      <div className="font-heading font-semibold">{label}</div>
      <p className="text-sm text-muted-foreground">Type your full name exactly as shown: <b className="text-foreground" data-testid="sign-expected-name">{user.name}</b></p>
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" className="h-11" data-testid="sign-name-input" />
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={agree} onCheckedChange={setAgree} data-testid="sign-agree-checkbox" />I agree and sign electronically. My name, time, IP address and account are recorded.</label>
      {err && <div className="text-sm font-medium text-primary" data-testid="sign-error">{err}</div>}
      <Button className="h-11 w-full sm:w-auto" disabled={busy} onClick={go} data-testid="sign-submit-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Sign</Button>
    </div>
  );
}

export function Checklist({ items, testid }) {
  return (
    <ul className="space-y-2" data-testid={testid}>
      {items.map((c) => <li key={c.key} className="flex items-center gap-2 text-sm" data-testid={`file-check-${c.key}`} data-present={c.present}>
        <CheckCircle2 className={`h-4 w-4 ${c.present ? "text-[hsl(var(--positive))]" : "text-muted-foreground/40"}`} />{c.label}
        <span className="ml-auto text-xs text-muted-foreground">{c.present ? "Present" : "Missing"}</span></li>)}
    </ul>
  );
}
